import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import moment from "moment";
import { Link } from "react-router-dom";

const UserBlockedListPage = ({ userId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const navigate = useNavigate();
    const authToken = localStorage.getItem("authToken");

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, itemsPerPage, userId]);

    const fetchData = async (page) => {
        const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/${userId}/blocked-users?page=${page}&limit=${itemsPerPage}`;

        try {
            setLoading(true);
            const response = await fetch(API_ENDPOINT, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (response.status === 401) {
                handleUnauthorized(navigate);
                return;
            }

            if (!response.ok) throw new Error("Failed to fetch blocked users");

            const res = await response.json();

            setData(res.data || []);
            setTotalItems(res.pagination?.total || 0);
            setTotalPages(res.pagination?.totalPages || 1);
        } catch (error) {
            setData([]);
            setErrorMessage(error.message);
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleNext = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
    const handlePrev = () => currentPage > 1 && setCurrentPage((p) => p - 1);

    const getPaginationRange = () => {
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    return (
        <div className="card h-100 p-0 radius-12">
            {successMessage && (
                <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
            )}
            {errorMessage && (
                <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
            )}

            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                    >
                        <option value="10">10 items per page</option>
                        <option value="20">20 items per page</option>
                        <option value="50">50 items per page</option>
                        <option value="100">100 items per page</option>
                    </select>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Blocked At</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        <span className="loader-15"></span>
                                    </td>
                                </tr>
                            ) : data.length > 0 ? (
                                data.map((item) => (
                                    <tr key={item.blockId}>
                                        <td>{item.fullName}</td>
                                        <td>{item.email}</td>
                                        <td>
                                            <span className="badge bg-success">{item.status}</span>
                                        </td>
                                        <td>
                                            {moment(item.blockedAt).format("DD-MM-YYYY, hh:mm A")}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        No Blocked Users Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                    <span>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    </span>

                    <ul className="pagination d-flex gap-2">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#" onClick={handlePrev}>
                                <Icon icon="ep:d-arrow-left" />
                            </Link>
                        </li>

                        {getPaginationRange().map((page) => (
                            <li key={page} className="page-item">
                                <Link
                                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                                        ? "bg-primary-600 text-white"
                                        : "bg-neutral-200"
                                        }`}
                                    to="#"
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </Link>
                            </li>
                        ))}

                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#" onClick={handleNext}>
                                <Icon icon="ep:d-arrow-right" />
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default UserBlockedListPage;
